import React from "react";
import { Dialog } from "@mui/material";
import { X, Check, Feather, BookOpen } from "@phosphor-icons/react";

interface WritingStyleModalProps {
  open: boolean;
  onClose: () => void;
  currentStyle: "General" | "Narrative";
  onStyleChange: (style: "General" | "Narrative") => void;
  disabled?: boolean;
}

const WritingStyleModal: React.FC<WritingStyleModalProps> = ({
  open,
  onClose,
  currentStyle,
  onStyleChange,
  disabled = false,
}) => {
  const handleStyleSelect = (style: "General" | "Narrative") => {
    if (!disabled) {
      onStyleChange(style);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          width: "480px",
          maxWidth: "90vw",
        },
      }}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Writing style</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-6">
          Choose how AI-generated answers are written in your report.
        </p>

        {/* Style Options */}
        <div className="space-y-3">
          {/* General Style */}
          <button
            onClick={() => handleStyleSelect("General")}
            disabled={disabled}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${
              currentStyle === "General"
                ? "border-gray-800 bg-gray-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div
                  className={`p-2 rounded-lg ${currentStyle === "General" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600"}`}
                >
                  <Feather size={20} />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">General</h3>
                  <p className="text-sm text-gray-600">
                    Clear and concise responses. Best for factual content,
                    summaries, and straightforward analysis.
                  </p>
                </div>
              </div>
              {currentStyle === "General" && (
                <Check className="w-5 h-5 text-gray-800 flex-shrink-0 mt-1" />
              )}
            </div>
          </button>

          {/* Narrative Style */}
          <button
            onClick={() => handleStyleSelect("Narrative")}
            disabled={disabled}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${
              currentStyle === "Narrative"
                ? "border-gray-800 bg-gray-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div
                  className={`p-2 rounded-lg ${currentStyle === "Narrative" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600"}`}
                >
                  <BookOpen size={20} />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">Narrative</h3>
                  <p className="text-sm text-gray-600">
                    Flowing, story-like responses. Best for executive summaries,
                    investment memos, and reports requiring narrative flow.
                  </p>
                </div>
              </div>
              {currentStyle === "Narrative" && (
                <Check className="w-5 h-5 text-gray-800 flex-shrink-0 mt-1" />
              )}
            </div>
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default WritingStyleModal;
